import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { fmtRelative } from "@/lib/format";
import { Check, X as XIcon, Mail, Save, UserCheck, ShieldCheck, Loader2 } from "lucide-react";

const STATUSES = ["pending", "approved", "rejected", "all"];

export default function AdminWaitlist() {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  const [search, setSearch] = useState("");
  const [settings, setSettings] = useState({});
  const [savingSettings, setSavingSettings] = useState(false);
  const [approving, setApproving] = useState(null);   // user obj being approved via dialog
  const [approveBusy, setApproveBusy] = useState(false);
  const [approvePreset, setApprovePreset] = useState("full");
  const [approveLockedGroups, setApproveLockedGroups] = useState([]);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/waitlist?status=${status}`);
      setRows(data || []);
    } finally { setBusy(false); }
  };
  const loadSettings = async () => {
    const { data } = await api.get("/admin/system/settings");
    setSettings(data || {});
  };

  useEffect(() => { load(); }, [status]);
  useEffect(() => { loadSettings(); }, []);

  const approve = async (u) => {
    // Iter 253f · Open the role-picker dialog. Defaults inherit from platform settings.
    setApproving(u);
    setApprovePreset(settings.waitlist_default_role_preset || "full");
    setApproveLockedGroups(settings.waitlist_limited_locked_groups || []);
  };

  const confirmApprove = async () => {
    if (!approving) return;
    setApproveBusy(true);
    try {
      const body = { role_preset: approvePreset };
      if (approvePreset === "limited") body.locked_groups = approveLockedGroups;
      const { data } = await api.post(`/admin/waitlist/${approving.id}/approve`, body);
      const msg = data?.role_preset === "limited"
        ? `${approving.name || approving.email} approved · Limited (${(data.locked_groups || []).length} feature group(s) locked)`
        : `${approving.name || approving.email} approved · Full access`;
      toast.success(msg);
      setApproving(null); setApproveBusy(false);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Approve failed"));
      setApproveBusy(false);
    }
  };
  const reject = async (u) => {
    const reason = window.prompt(`Reject ${u.name || u.email}? Optional reason:`, "") ?? null;
    if (reason === null) return;
    try { await api.post(`/admin/waitlist/${u.id}/reject`, { reason }); toast.success("User rejected — email dispatched"); load(); }
    catch (e) { toast.error(apiErrorMessage(e, "Reject failed")); }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const patch = {
        waitlist_enabled: !!settings.waitlist_enabled,
        waitlist_message: settings.waitlist_message,
        waitlist_email_subject: settings.waitlist_email_subject,
        waitlist_email_body: settings.waitlist_email_body,
        waitlist_reject_subject: settings.waitlist_reject_subject,
        waitlist_reject_body: settings.waitlist_reject_body,
        waitlist_default_role_preset: settings.waitlist_default_role_preset || "full",
        waitlist_limited_locked_groups: settings.waitlist_limited_locked_groups || [],
      };
      await api.patch("/admin/system/settings", patch);
      toast.success("Waitlist settings saved");
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSavingSettings(false); }
  };

  const toggleLockedGroup = (target, key) => {
    if (target === "settings") {
      const list = settings.waitlist_limited_locked_groups || [];
      const next = list.includes(key) ? list.filter((k) => k !== key) : [...list, key];
      setSettings({ ...settings, waitlist_limited_locked_groups: next });
    } else {
      setApproveLockedGroups((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
    }
  };

  const [presetFilter, setPresetFilter] = useState("all");   // all|full|limited|unset

  const filtered = rows.filter((u) => {
    if (presetFilter !== "all") {
      const p = u.default_workspace_role_preset || "unset";
      if (presetFilter === "unset" && p !== "unset") return false;
      if (presetFilter !== "unset" && p !== presetFilter) return false;
    }
    if (!search) return true;
    const q = search.toLowerCase();
    return (u.email || "").toLowerCase().includes(q) || (u.name || "").toLowerCase().includes(q);
  });

  const counts = {
    pending: rows.filter((r) => r.signup_status === "pending_approval").length,
    approved: rows.filter((r) => r.signup_status === "active").length,
    rejected: rows.filter((r) => r.signup_status === "rejected").length,
  };

  return (
    <>
      <PageHeader
        eyebrow="Super Admin"
        title="Waitlist & Users"
        description="Approve new signups, search the user directory, and configure the waitlist communication."
        testid="admin-waitlist-header"
      />
      <div className="px-4 sm:px-6 md:px-10 py-6 space-y-6">
        <Tabs defaultValue="queue" className="w-full">
          <TabsList className="rounded-none border border-border bg-muted/30">
            <TabsTrigger value="queue" data-testid="waitlist-tab-queue">Queue</TabsTrigger>
            <TabsTrigger value="settings" data-testid="waitlist-tab-settings">Settings &amp; templates</TabsTrigger>
          </TabsList>

          <TabsContent value="queue" className="m-0 space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Pending" value={status === "pending" ? rows.length : counts.pending} tone="warning" />
              <Stat label="Approved" value={status === "approved" ? rows.length : counts.approved} tone="success" />
              <Stat label="Rejected" value={status === "rejected" ? rows.length : counts.rejected} tone="muted" />
              <Stat label="Showing" value={filtered.length} tone="muted" />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {STATUSES.map((s) => (
                <Button
                  key={s}
                  size="sm"
                  variant={status === s ? "default" : "outline"}
                  onClick={() => setStatus(s)}
                  className="rounded-none h-8 text-xs capitalize"
                  data-testid={`waitlist-filter-${s}`}
                >
                  {s}
                </Button>
              ))}
              <Input
                placeholder="Search name or email…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-8 w-64 ml-auto"
                data-testid="waitlist-search-input"
              />
              <select value={presetFilter} onChange={(e) => setPresetFilter(e.target.value)}
                        className="h-8 border border-border text-xs px-2 bg-background"
                        data-testid="waitlist-preset-filter">
                <option value="all">All roles</option>
                <option value="full">Full Access</option>
                <option value="limited">Limited</option>
                <option value="unset">Unset (older users)</option>
              </select>
            </div>

            <div className="border border-border" data-testid="waitlist-list">
              <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-muted/40 text-[10px] uppercase tracking-widest text-foreground/60">
                <div className="col-span-3">Name</div>
                <div className="col-span-3">Email</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-2">Role preset</div>
                <div className="col-span-2 text-right">Actions</div>
              </div>
              {busy && <div className="px-4 py-6 text-sm text-foreground/60">Loading…</div>}
              {!busy && filtered.length === 0 && (
                <div className="px-4 py-10 text-center text-sm text-foreground/60" data-testid="waitlist-empty">
                  No users in this view.
                </div>
              )}
              {filtered.map((u) => (
                <div key={u.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-t border-border items-center text-sm" data-testid={`waitlist-row-${u.id}`}>
                  <div className="col-span-3 font-medium truncate">{u.name || "—"}</div>
                  <div className="col-span-3 text-foreground/80 truncate">{u.email}</div>
                  <div className="col-span-2">
                    <span className={`chip text-[10px] ${u.signup_status === "pending_approval" ? "chip-open" : u.signup_status === "rejected" ? "chip-rejected" : "chip-resolved"}`}>
                      {u.signup_status === "pending_approval" ? "pending" : u.signup_status}
                    </span>
                  </div>
                  <div className="col-span-2" data-testid={`waitlist-preset-${u.id}`}>
                    <PresetBadge preset={u.default_workspace_role_preset}
                                    locked={u.default_workspace_locked_groups || []} />
                  </div>
                  <div className="col-span-2 flex gap-1 justify-end">
                    {u.signup_status === "pending_approval" && (
                      <>
                        <Button size="sm" className="rounded-none h-7 text-xs" onClick={() => approve(u)} data-testid={`waitlist-approve-${u.id}`}>
                          <Check className="w-3 h-3 mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" onClick={() => reject(u)} data-testid={`waitlist-reject-${u.id}`}>
                          <XIcon className="w-3 h-3 mr-1" /> Reject
                        </Button>
                      </>
                    )}
                    {u.signup_status === "rejected" && (
                      <Button size="sm" className="rounded-none h-7 text-xs" onClick={() => approve(u)} data-testid={`waitlist-reapprove-${u.id}`}>
                        <UserCheck className="w-3 h-3 mr-1" /> Re-approve
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="settings" className="m-0 space-y-4">
            <div className="border border-border p-6 space-y-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="label-eyebrow mb-1">Master switch</div>
                  <div className="font-semibold">Waitlist enabled</div>
                  <p className="text-xs text-foreground/60 mt-1 max-w-md">When ON, every new signup lands in the queue. Switch OFF for instant auto-approval like a typical SaaS signup.</p>
                </div>
                <Switch
                  checked={!!settings.waitlist_enabled}
                  onCheckedChange={(v) => setSettings({ ...settings, waitlist_enabled: v })}
                  data-testid="waitlist-toggle"
                />
              </div>
              <div>
                <Label className="label-eyebrow text-[10px] block mb-2">Thank-you message shown on signup</Label>
                <Textarea
                  rows={2}
                  value={settings.waitlist_message || ""}
                  onChange={(e) => setSettings({ ...settings, waitlist_message: e.target.value })}
                  data-testid="waitlist-message-input"
                />
              </div>
            </div>

            <div className="border border-border p-6 space-y-4" data-testid="waitlist-role-panel">
              <div>
                <div className="label-eyebrow inline-flex items-center gap-2"><ShieldCheck className="w-3 h-3" /> Default role on approval</div>
                <p className="text-xs text-foreground/60 mt-1 max-w-2xl">
                  Choose what the approved user gets by default when they create their first workspace. You can override per user at the moment you click <b>Approve</b>.
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <RolePresetCard
                  active={settings.waitlist_default_role_preset === "full" || !settings.waitlist_default_role_preset}
                  onClick={() => setSettings({ ...settings, waitlist_default_role_preset: "full" })}
                  title="Full Access"
                  description="Workspace Owner with every feature unlocked. Best for enterprise plans or trusted partners."
                  testid="preset-card-full"
                />
                <RolePresetCard
                  active={settings.waitlist_default_role_preset === "limited"}
                  onClick={() => setSettings({ ...settings, waitlist_default_role_preset: "limited" })}
                  title="Limited"
                  description="Workspace Owner with newer/enterprise feature groups locked out. Toggle exactly which groups below."
                  testid="preset-card-limited"
                />
              </div>
              {settings.waitlist_default_role_preset === "limited" && (
                <div className="border border-amber-300 bg-amber-50/30 p-4 space-y-2" data-testid="feature-groups-panel">
                  <div className="label-eyebrow">Feature groups to LOCK OUT for Limited users</div>
                  <p className="text-[11px] text-foreground/60">Check any group to hide its permissions from the Workspace Owner + Admin roles at workspace-creation time.</p>
                  <div className="grid md:grid-cols-2 gap-2">
                    {(settings._feature_groups || []).map((g) => {
                      const on = (settings.waitlist_limited_locked_groups || []).includes(g.key);
                      return (
                        <label key={g.key} className={`flex items-start gap-2 border p-2 cursor-pointer transition-colors ${on ? "border-amber-500 bg-amber-100/40" : "border-border hover:border-amber-400"}`} data-testid={`feature-group-${g.key}`}>
                          <input type="checkbox" checked={on}
                                    onChange={() => toggleLockedGroup("settings", g.key)}
                                    className="mt-0.5"
                                    data-testid={`feature-group-check-${g.key}`} />
                          <div className="flex-1">
                            <div className="text-xs font-semibold">{g.label}</div>
                            <div className="text-[10px] text-foreground/60">{g.description}</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <PlanPresetMapPanel settings={settings} featureGroups={settings._feature_groups || []} />

            <RetroApplyPanel settings={settings} />

            <div className="grid md:grid-cols-2 gap-3">
              <EmailTemplate
                title="Approval email"
                subjectKey="waitlist_email_subject"
                bodyKey="waitlist_email_body"
                settings={settings} setSettings={setSettings}
                testid="waitlist-approve-email"
              />
              <EmailTemplate
                title="Rejection email"
                subjectKey="waitlist_reject_subject"
                bodyKey="waitlist_reject_body"
                settings={settings} setSettings={setSettings}
                testid="waitlist-reject-email"
              />
            </div>

            <div className="flex justify-end">
              <Button onClick={saveSettings} disabled={savingSettings} className="rounded-none" data-testid="waitlist-save-settings-btn">
                <Save className="w-4 h-4 mr-2" /> {savingSettings ? "Saving…" : "Save settings"}
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {approving && (
        <div className="fixed inset-0 z-50 bg-background/80 flex items-center justify-center" data-testid="approve-dialog">
          <div className="bg-background border border-border p-5 w-[560px] space-y-4 shadow-lg max-h-[85vh] overflow-y-auto">
            <div>
              <div className="text-lg font-bold">Approve {approving.name || approving.email}</div>
              <p className="text-xs text-foreground/60 mt-1">Choose the role catalog they&apos;ll receive when they create their first workspace.</p>
            </div>
            <div className="grid md:grid-cols-2 gap-2">
              <RolePresetCard
                active={approvePreset === "full"}
                onClick={() => setApprovePreset("full")}
                title="Full Access"
                description="Every feature unlocked."
                testid="approve-preset-full"
              />
              <RolePresetCard
                active={approvePreset === "limited"}
                onClick={() => setApprovePreset("limited")}
                title="Limited"
                description="Selected feature groups locked out below."
                testid="approve-preset-limited"
              />
            </div>
            {approvePreset === "limited" && (
              <div className="border border-amber-300 bg-amber-50/30 p-3 space-y-2" data-testid="approve-feature-groups">
                <div className="text-[11px] font-semibold">Groups to lock out for this user</div>
                <div className="grid md:grid-cols-2 gap-1.5 max-h-56 overflow-y-auto">
                  {(settings._feature_groups || []).map((g) => {
                    const on = approveLockedGroups.includes(g.key);
                    return (
                      <label key={g.key} className={`flex items-start gap-2 border p-2 cursor-pointer text-[11px] ${on ? "border-amber-500 bg-amber-100/40" : "border-border"}`} data-testid={`approve-group-${g.key}`}>
                        <input type="checkbox" checked={on}
                                  onChange={() => toggleLockedGroup("approve", g.key)}
                                  className="mt-0.5" data-testid={`approve-group-check-${g.key}`} />
                        <div>
                          <div className="font-semibold">{g.label}</div>
                          <div className="text-foreground/60">{g.description}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
              <Button variant="outline" size="sm" className="rounded-none" onClick={() => setApproving(null)} data-testid="approve-cancel">
                Cancel
              </Button>
              <Button size="sm" className="rounded-none" onClick={confirmApprove} disabled={approveBusy} data-testid="approve-confirm">
                {approveBusy ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Approving…</> : <><Check className="w-3 h-3 mr-1" /> Approve &amp; email</>}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Stat({ label, value, tone }) {
  const toneMap = { warning: "border-l-yellow-500", success: "border-l-green-600", muted: "border-l-foreground/30" };
  return (
    <div className={`border border-border border-l-4 ${toneMap[tone] || ""} p-4`}>
      <div className="text-[10px] uppercase tracking-widest text-foreground/60">{label}</div>
      <div className="text-2xl font-bold mt-1" style={{ fontFamily: "var(--font-heading)" }}>{value}</div>
    </div>
  );
}

function PresetBadge({ preset, locked }) {
  if (!preset) return <span className="text-[10px] text-foreground/40">—</span>;
  if (preset === "full") {
    return <span className="text-[10px] px-1.5 py-0.5 border border-emerald-500 bg-emerald-50 text-emerald-800 whitespace-nowrap">Full Access</span>;
  }
  const lockedCount = (locked || []).length;
  return (
    <span title={locked?.join(", ")}
           className="text-[10px] px-1.5 py-0.5 border border-amber-500 bg-amber-50 text-amber-800 whitespace-nowrap">
      Limited · {lockedCount} locked
    </span>
  );
}


function RolePresetCard({ active, onClick, title, description, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
             className={`text-left border p-3 transition-colors ${active ? "border-primary bg-primary/5" : "border-border hover:border-primary/60"}`}>
      <div className="text-sm font-bold flex items-center gap-2">
        {active && <span className="text-primary text-xs">●</span>}
        {title}
      </div>
      <div className="text-[11px] text-foreground/60 mt-1">{description}</div>
    </button>
  );
}


function EmailTemplate({ title, subjectKey, bodyKey, settings, setSettings, testid }) {
  return (
    <div className="border border-border p-5 space-y-3">
      <div className="label-eyebrow inline-flex items-center gap-2"><Mail className="w-3 h-3" /> {title}</div>
      <div>
        <Label className="text-[10px] uppercase tracking-widest text-foreground/60 mb-1 block">Subject</Label>
        <Input value={settings[subjectKey] || ""} onChange={(e) => setSettings({ ...settings, [subjectKey]: e.target.value })} className="h-9" data-testid={`${testid}-subject`} />
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-widest text-foreground/60 mb-1 block">Body</Label>
        <Textarea rows={6} value={settings[bodyKey] || ""} onChange={(e) => setSettings({ ...settings, [bodyKey]: e.target.value })} data-testid={`${testid}-body`} />
        <p className="text-[10px] text-foreground/50 mt-1">Tokens: <code>{`{{name}}`}</code>, <code>{`{{platform_name}}`}</code></p>
      </div>
    </div>
  );
}

function PlanPresetMapPanel({ settings, featureGroups }) {
  const [map, setMap] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/waitlist/plan-preset-map").then(({ data }) => {
      setMap(data?.plan_preset_map || {});
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const KNOWN_PLANS = ["free", "starter", "pro", "enterprise"];
  const rows = Array.from(new Set([...KNOWN_PLANS, ...Object.keys(map)]));

  const upsert = (plan, preset, locked) => {
    setMap((prev) => ({
      ...prev,
      [plan]: { role_preset: preset, locked_groups: locked || [] },
    }));
  };
  const remove = (plan) => {
    setMap((prev) => { const n = { ...prev }; delete n[plan]; return n; });
  };
  const save = async () => {
    setSaving(true);
    try {
      const body = Object.entries(map).map(([plan_slug, v]) => ({
        plan_slug, role_preset: v.role_preset, locked_groups: v.locked_groups || [],
      }));
      await api.put("/admin/waitlist/plan-preset-map", body);
      toast.success("Plan → preset map saved");
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSaving(false); }
  };

  return (
    <div className="border border-border p-6 space-y-3" data-testid="plan-preset-map-panel">
      <div>
        <div className="label-eyebrow inline-flex items-center gap-2">🎯 Per-plan role preset</div>
        <p className="text-xs text-foreground/60 mt-1 max-w-2xl">
          Bind billing plans to a role preset — e.g. <b>Starter → Limited</b>, <b>Pro/Enterprise → Full</b>. When a workspace is created on a mapped plan, the role catalog auto-locks to the chosen preset. Falls back to the per-user default from approval when no plan mapping exists.
        </p>
      </div>
      {!loaded ? <div className="text-xs text-foreground/60">Loading…</div> : (
        <div className="space-y-2">
          {rows.map((plan) => {
            const entry = map[plan] || {};
            const preset = entry.role_preset || "unset";
            const locked = entry.locked_groups || [];
            return (
              <div key={plan} className="border border-border p-2 space-y-1" data-testid={`plan-row-${plan}`}>
                <div className="flex items-center gap-2 text-xs">
                  <div className="w-24 font-semibold uppercase">{plan}</div>
                  <select value={preset}
                             onChange={(e) => {
                               const v = e.target.value;
                               if (v === "unset") remove(plan);
                               else upsert(plan, v, locked);
                             }}
                             className="border border-border h-7 px-1 bg-background"
                             data-testid={`plan-preset-select-${plan}`}>
                    <option value="unset">— unset —</option>
                    <option value="full">Full Access</option>
                    <option value="limited">Limited</option>
                  </select>
                  {preset === "limited" && (
                    <span className="text-[10px] text-foreground/60">
                      {locked.length ? `${locked.length} groups locked` : "No groups locked (edit below)"}
                    </span>
                  )}
                </div>
                {preset === "limited" && (
                  <div className="grid md:grid-cols-4 gap-1 pl-24">
                    {featureGroups.map((g) => {
                      const on = locked.includes(g.key);
                      return (
                        <label key={g.key} className={`text-[10px] flex items-center gap-1 border p-1 cursor-pointer ${on ? "border-amber-500 bg-amber-50" : "border-border"}`}>
                          <input type="checkbox" checked={on}
                                    onChange={() => {
                                      const next = on ? locked.filter((k) => k !== g.key) : [...locked, g.key];
                                      upsert(plan, "limited", next);
                                    }}
                                    data-testid={`plan-${plan}-group-${g.key}`} />
                          <span>{g.label}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          <div className="flex justify-end">
            <Button size="sm" className="rounded-none" onClick={save} disabled={saving}
                        data-testid="plan-preset-map-save">
              <Save className="w-3 h-3 mr-1" /> {saving ? "Saving…" : "Save plan → preset map"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}


function RetroApplyPanel({ settings }) {
  const [dryResult, setDryResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const runDry = async () => {
    setBusy(true); setDryResult(null);
    try {
      const { data } = await api.post("/admin/waitlist/retro-apply", { dry_run: true });
      setDryResult(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setBusy(false); }
  };
  const commit = async () => {
    if (!window.confirm(`This will overwrite permissions on ${dryResult?.affected_role_count || 0} workspace roles. This CANNOT be un-done automatically. Continue?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/waitlist/retro-apply", { dry_run: false });
      toast.success(`Applied · ${data.applied_role_count} role(s) updated`);
      setDryResult(null);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setBusy(false); }
  };

  const preset = settings.waitlist_default_role_preset || "full";
  const locked = settings.waitlist_limited_locked_groups || [];

  return (
    <div className="border border-border p-6 space-y-3" data-testid="retro-apply-panel">
      <div>
        <div className="label-eyebrow inline-flex items-center gap-2">♻️ Retro-apply to existing workspaces</div>
        <p className="text-xs text-foreground/60 mt-1 max-w-2xl">
          Re-seed <b>every</b> existing workspace&apos;s Owner + Admin roles to match the current default preset (<b>{preset === "limited" ? `Limited · ${locked.length} groups locked` : "Full Access"}</b>). Super-admin owned workspaces are skipped. Always dry-run first.
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" className="rounded-none" onClick={runDry} disabled={busy}
                     data-testid="retro-apply-dry-btn">
          {busy && !dryResult ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
          Dry run
        </Button>
        {dryResult && (
          <Button size="sm" className="rounded-none bg-rose-500 hover:bg-rose-600" onClick={commit} disabled={busy}
                       data-testid="retro-apply-commit-btn">
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
            Apply to {dryResult.affected_role_count} role(s)
          </Button>
        )}
      </div>
      {dryResult && (
        <div className="border border-amber-300 bg-amber-50/30 p-3 text-xs space-y-1" data-testid="retro-apply-dry-result">
          <div><b>{dryResult.affected_role_count}</b> role(s) will change. Preset: <b>{dryResult.preset}</b>{dryResult.locked_groups?.length ? ` · Locked: ${dryResult.locked_groups.join(", ")}` : ""}</div>
          {dryResult.sample?.length > 0 && (
            <ul className="pl-4 list-disc max-h-40 overflow-y-auto">
              {dryResult.sample.map((s, i) => (
                <li key={i}>
                  <b>{s.workspace_name}</b> · {s.role_slug} · +{s.will_add} / -{s.will_remove} perms
                </li>
              ))}
              {dryResult.affected_role_count > dryResult.sample.length && (
                <li className="italic">… and {dryResult.affected_role_count - dryResult.sample.length} more</li>
              )}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

